import React, { useContext, useState, useEffect, useRef, FC } from "react";
import { Stepper } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks"; // Import the hook for responsiveness
import { useLocation, useNavigate } from "react-router-dom";
import { UserContext } from "@/contexts/UserContext/UserContext";
import StudyNameStep from "./StudyNameStep";
import ChooseLockedShipsStep from "./ChooseLockedShipsStep";
import WindPropulsionPage from "./WindPropulsionPage";
import LaunchProcessStep from "./LaunchProcessStep";
import { useDarkModeContext } from "@/hooks/contextHooks/useDarkModeContext";
import { getStepperStyles } from "@/helpers/getStepperStyles";
import { usePageTransition } from "@/hooks/helperHooks/usePageTransition";
import { useFetchProjects } from "@/hooks/apiHooks/useFetchProjects";
import { useFetchUserShips } from "@/hooks/apiHooks/useFetchUserShips";
import { useFetchSailModels } from "@/hooks/apiHooks/useFetchSailModels";
import { useFetchUserIP } from "@/hooks/apiHooks/useFetchUserIP";
import styles from "../../css/study.module.css";
import { useAppColors } from "@/hooks/contextHooks/useAppColors";

export interface Validatable {
  validate: () => boolean;
}

const StudyStepper: FC = () => {
  const transition = usePageTransition();
  const { colors } = useAppColors();
  const [active, setActive] = useState(0);
  const context = useContext(UserContext);
  const location = useLocation();
  const [highestStepVisited, setHighestStepVisited] = useState(active);
  const [processCompleted, setProcessCompleted] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);
  const { isDarkMode } = useDarkModeContext();

  const isMobile = useMediaQuery("(max-width: 800px)");

  const studyNameRef = useRef<Validatable | null>(null);
  const chooseLockedShipsRef = useRef<Validatable | null>(null);
  const windPropulsionRef = useRef<Validatable | null>(null);
  const launchProcessRef = useRef<any | null>(null);
  const navigate = useNavigate();

  const { data: projects = [], isLoading: isLoadingProjects } =
    useFetchProjects();
  const { data: ships = [], isLoading: isLoadingShips } = useFetchUserShips();
  const { data: sailModels = [], isLoading: isLoadingSailModels } =
    useFetchSailModels();
  const { data: userIp, isLoading: isLoadingUserIp } = useFetchUserIP();

  const handleStepChange = (nextStep: number) => {
    const isOutOfBounds = nextStep > 3 || nextStep < 0;
    if (isOutOfBounds) {
      return;
    }
    setActive(nextStep);
    setHighestStepVisited((hSC) => Math.max(hSC, nextStep));
  };

  const shouldAllowSelectStep = (step: number) =>
    highestStepVisited >= step && active !== step;

  const handleNext = () => {
    let isValid = true;
    if (active === 0 && studyNameRef.current) {
      isValid = studyNameRef.current.validate();
    } else if (active === 1 && chooseLockedShipsRef.current) {
      isValid = chooseLockedShipsRef.current.validate();
    } else if (active === 2 && windPropulsionRef.current) {
      isValid = windPropulsionRef.current.validate();
    }
    if (isValid) {
      handleStepChange(active + 1);
    }
  };

  const handleProcessComplete = () => {
    setProcessCompleted(true);
    setIsLaunching(false);
  };

  const handleLaunch = () => {
    const isValid = launchProcessRef.current?.validate();
    if (isValid) {
      setIsLaunching(true);
      setProcessCompleted(false);
      launchProcessRef.current?.handleLaunch();
    }
  };

  const handleRelaunch = () => {
    navigate("/");
  };

  useEffect(() => {
    if (!location.pathname.startsWith("/add-study")) {
      context?.setStudyFormValues({});
    }
  }, [location, context]);

  const buttonContainerStyles: React.CSSProperties = {
    ...(isMobile && {
      flexDirection: "column",
      gap: "0.75rem",
      padding: "1rem",
    }),
  };

  const buttonStyles: React.CSSProperties = {
    ...(isMobile && {
      width: "100%",
    }),
  };

  if (
    transition ||
    isLoadingProjects ||
    isLoadingShips ||
    isLoadingSailModels ||
    isLoadingUserIp ||
    !userIp
  )
    return null;

  return (
    <div className={styles.container}>
      <Stepper
        active={active}
        onStepClick={handleStepChange}
        style={{ padding: isMobile ? "1rem 1.5rem" : "0 5vw", width: "100%" }}
        orientation={isMobile ? "vertical" : "horizontal"}
        styles={getStepperStyles(isDarkMode)}
      >
        <Stepper.Step
          label="General study information"
          description="Study Name"
          allowStepSelect={shouldAllowSelectStep(0)}
        >
          <StudyNameStep ref={studyNameRef} projects={projects} />
        </Stepper.Step>
        <Stepper.Step
          label="Vessel selection"
          description="Choose Locked Ships"
          allowStepSelect={shouldAllowSelectStep(1)}
        >
          <ChooseLockedShipsStep ref={chooseLockedShipsRef} ships={ships} />
        </Stepper.Step>
        <Stepper.Step
          label="Wind propulsion"
          description="Position wind propellers"
          allowStepSelect={shouldAllowSelectStep(2)}
        >
          <WindPropulsionPage ref={windPropulsionRef} sailModels={sailModels} />
        </Stepper.Step>
        <Stepper.Step
          label="Processing"
          description="Launch Process"
          allowStepSelect={shouldAllowSelectStep(3)}
        >
          <LaunchProcessStep
            ref={launchProcessRef}
            onComplete={handleProcessComplete}
            userIp={userIp}
          />
        </Stepper.Step>
        <Stepper.Completed>Completed</Stepper.Completed>
      </Stepper>

      <div>
        {active === 3 ? (
          !processCompleted ? (
            <div
              className={styles.buttonContainer}
              style={buttonContainerStyles}
            >
              <div
                className={styles.btn}
                style={{
                  ...buttonStyles,
                  backgroundColor: isDarkMode ? colors.secondary : colors.brand,
                  color: colors.primary,
                }}
                onClick={
                  isLaunching ? () => null : () => handleStepChange(active - 1)
                }
              >
                Back
              </div>

              <div
                className={styles.btn}
                style={{
                  ...buttonStyles,
                  backgroundColor: isDarkMode ? colors.secondary : colors.brand,
                  color: colors.primary,
                }}
                onClick={
                  isLaunching || launchProcessRef.current?.isLaunchDisabled
                    ? () => null
                    : handleLaunch
                }
              >
                {isLaunching ? "Launching..." : "Launch Process"}
              </div>
            </div>
          ) : (
            <div
              className={styles.buttonContainer}
              style={buttonContainerStyles}
            >
              <div
                className={styles.btn}
                style={{
                  ...buttonStyles,
                  backgroundColor: isDarkMode ? colors.secondary : colors.brand,
                  color: colors.primary,
                }}
                onClick={
                  !processCompleted
                    ? () => null
                    : () => launchProcessRef.current?.handleDownloadConfig()
                }
              >
                Download Config
              </div>

              <div
                className={styles.btn}
                style={{
                  ...buttonStyles,
                  backgroundColor: isDarkMode ? colors.secondary : colors.brand,
                  color: colors.primary,
                }}
                onClick={
                  !processCompleted
                    ? () => null
                    : () => launchProcessRef.current?.handleVisualizePolar()
                }
              >
                Visualize Polar
              </div>

              <div
                className={styles.btn}
                style={{
                  ...buttonStyles,
                  backgroundColor: isDarkMode ? colors.secondary : colors.brand,
                  color: colors.primary,
                }}
                onClick={
                  isLaunching || !processCompleted ? () => null : handleRelaunch
                }
              >
                Relaunch New Study
              </div>
            </div>
          )
        ) : (
          <div className={styles.buttonContainer} style={buttonContainerStyles}>
            <div
              className={styles.btn}
              style={{
                ...buttonStyles,
                backgroundColor: isDarkMode
                  ? colors.secondary
                  : colors.darkGray,
                color: colors.primary,
              }}
              onClick={() =>
                active === 0 ? navigate("/") : handleStepChange(active - 1)
              }
            >
              {active === 0 ? "Back to Home" : "Back"}
            </div>

            <div
              className={styles.btn}
              style={{
                ...buttonStyles,
                backgroundColor: isDarkMode ? colors.secondary : colors.brand,
                color: colors.primary,
              }}
              onClick={handleNext}
            >
              Next step
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudyStepper;
